import * as React from "react";
import { Box, Button, Container, Dialog, DialogContent, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { useLogout, useMe } from "@/api/hooks";
import { useOrgLegal } from "@/api/legal";
import {
  AcceptOrgTermsButton,
  OrgSwitchButton,
  OrgTermsDocuments,
} from "@/features/legal/orgTermsShared";
import { legalDay, useOrgName } from "@/features/legal/orgTermsText";

/** The blocking screen for an admin of a ``lapsed`` org (spec §9.1),
 *  like ``TermsGate``: no ``onClose``. The ways on are accepting,
 *  switching org, deleting the org, billing and signing out. */
export function OrgTermsGate({ slug }: { slug: string }): React.JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const legal = useOrgLegal(slug);
  const logout = useLogout();
  const name = useOrgName(slug);
  const billing = Boolean(me.data?.features.billing_enabled);
  const date = legalDay(legal.data?.deadline_date);
  return (
    <Dialog open fullScreen aria-labelledby="org-terms-gate-title" data-testid="org-terms-gate">
      <DialogContent>
        <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
          <Box
            component="img"
            src="/brand/repoworks_logo_white.svg"
            alt=""
            aria-hidden
            sx={{ height: 32, width: "auto", mb: 3 }}
          />
          <Typography variant="h5" component="h1" id="org-terms-gate-title">
            {t("legal.org.gate.title")}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
            {name}
          </Typography>
          <Typography sx={{ mt: 2 }}>{t("legal.org.gate.body")}</Typography>
          {date && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t("legal.org.gate.deadline_passed", { date })}
            </Typography>
          )}
          {legal.data && (
            <Box sx={{ mt: 3 }}>
              <OrgTermsDocuments legal={legal.data} />
              <Box sx={{ mt: 3 }}>
                <AcceptOrgTermsButton slug={slug} legal={legal.data} />
              </Box>
            </Box>
          )}
          <Stack direction="row" sx={{ mt: 4, gap: 1, flexWrap: "wrap" }}>
            <OrgSwitchButton slug={slug} />
            <Button component={RouterLink} to="/app/admin/orgs" variant="text" color="error">
              {t("admin.orgs.delete_org")}
            </Button>
            {billing && (
              <Button component={RouterLink} to="/app/admin/billing" variant="text">
                {t("admin.tabs.billing")}
              </Button>
            )}
            <Button variant="text" color="inherit" onClick={() => logout.mutate()} disabled={logout.isPending}>
              {t("legal.gate.sign_out")}
            </Button>
          </Stack>
        </Container>
      </DialogContent>
    </Dialog>
  );
}
